import React from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { FaFilter, FaPlus, FaTimes, FaTrash } from "react-icons/fa";
import ConditionRow from "../../common/ConditionRow";
import DateLiteralsHintLink from "../../common/DateLiteralsHintLink";
import {
  getFieldByName,
  isCompleteListViewFilter,
  isOperatorValueRequired,
} from "../../../utils/conditionBuilderUtils";

/** True when list view logic is the simple combiners saved as literal AND / OR. */
function isSimpleListViewLogic(logic) {
  const u = String(logic ?? "").trim().toUpperCase();
  return u === "AND" || u === "OR";
}

function defaultCustomLogicExpression(validFilterCount) {
  if (validFilterCount <= 0) return "1";
  if (validFilterCount === 1) return "1";
  return Array.from({ length: validFilterCount }, (_, i) => String(i + 1)).join(" AND ");
}

const ListViewFilterPanel = ({
  externalFilters,
  externalFilterLogic,
  availableFields,
  filterValueFields = null,
  /** List object API name — scopes recordview lookups in filter value pickers. */
  objectName = null,
  /** While true, filter rows are covered so picklist/lookup options are not shown half-loaded. */
  resourcesLoading = false,
  /** Prefetched picklist options keyed by field id (from list view open). */
  picklistOptionsByFieldId = null,
  /** When false, title row is omitted (e.g. parent Modal provides Modal.Header). */
  showTitleBar = true,
  onClose,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  onPatchFilter,
  onUpdateLogic,
  onApplyFilters,
  onClearFilters,
  setShowDateHelpModal
}) => {
  const validFilterCount = externalFilters.filter(isCompleteListViewFilter).length;
  const logicSelectValue = isSimpleListViewLogic(externalFilterLogic)
    ? String(externalFilterLogic).trim().toUpperCase()
    : "CUSTOM";

  const onLogicModeChange = (e) => {
    const v = e.target.value;
    if (v === "CUSTOM") {
      onUpdateLogic(defaultCustomLogicExpression(validFilterCount));
    } else {
      onUpdateLogic(v);
    }
  };

  return (
    <div
      className="filter-sidebar-content listview-filter-panel-inner"
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: showTitleBar ? '20px' : '16px 20px 20px',
        width: '100%',
        minWidth: 0,
        ...(showTitleBar ? { maxHeight: '100%', overflow: 'hidden' } : {})
      }}
    >
      {showTitleBar && (
        <div className="filter-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e5e5e5' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#032d60', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFilter />
            Quick Filters
          </h3>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#706e6b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>
      )}

      <div
        className="filter-body position-relative"
        style={{
          flex: 1,
          overflowX: 'hidden',
          paddingRight: '8px',
          minHeight: 0,
          ...(showTitleBar ? { overflowY: 'auto' } : { overflowY: 'visible' })
        }}
      >
        {resourcesLoading && (
          <div
            className="position-absolute top-0 start-0 end-0 bottom-0 d-flex flex-column align-items-center justify-content-center gap-2"
            style={{
              background: 'rgba(255, 255, 255, 0.88)',
              zIndex: 3,
              backdropFilter: 'blur(1px)',
            }}
          >
            <Spinner animation="border" size="sm" variant="primary" role="status" />
            <span className="text-muted small text-center px-3">
              Loading field metadata and filter options…
            </span>
          </div>
        )}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Filter Logic</h6>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={onAddFilter}
              disabled={resourcesLoading}
            >
              <FaPlus className="me-1" />
              Add Filter
            </Button>
          </div>

          <Form.Select
            value={logicSelectValue}
            onChange={onLogicModeChange}
            className="mb-2"
            disabled={resourcesLoading}
          >
            <option value="AND">AND (All conditions must be true)</option>
            <option value="OR">OR (Any condition can be true)</option>
            <option value="CUSTOM">Custom (e.g. (1 OR 2) AND 3)</option>
          </Form.Select>
          {logicSelectValue === "CUSTOM" && (
            <div className="mb-3">
              <Form.Label className="small text-muted mb-1">Custom expression</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={String(externalFilterLogic ?? "")}
                onChange={(e) => onUpdateLogic(e.target.value)}
                placeholder="(1 OR 2) AND 3"
                className="font-monospace small"
                disabled={resourcesLoading}
              />
              <div className="small text-muted mt-1">
                Use numbers 1, 2, 3… for each filter row below, with AND, OR, and parentheses. This is applied on the server when the list loads.
              </div>
            </div>
          )}
        </div>

        {externalFilters.length === 0 ? (
          <div className="text-center text-muted py-4">
            <FaFilter className="mb-2" size={24} />
            <p>No filters configured</p>
            <small>Click &quot;Add Filter&quot; to create filtering rules</small>
          </div>
        ) : (
          <div className="filters-container">
            {externalFilters.map((filter, index) => {
              const selectedField = getFieldByName(availableFields, filter.field, { nameKey: 'field_name' });
              const dt = (selectedField?.data_type || '').toLowerCase();
              const showDateHint = selectedField && (dt === 'date' || dt === 'datetime') &&
                isOperatorValueRequired(filter.operator);

              return (
                <div key={filter.id} className="filter-row border rounded p-3 mb-3 bg-light">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">Filter {index + 1}</h6>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => onRemoveFilter(filter.id)}
                      disabled={resourcesLoading}
                    >
                      <FaTrash />
                    </Button>
                  </div>

                  <Row className="align-items-start">
                    <Col xs={12}>
                      <ConditionRow
                        condition={filter}
                        fields={availableFields}
                        onChange={(idx, key, val) => {
                          const row = externalFilters[idx];
                          if (row) onUpdateFilter(row.id, key, val);
                        }}
                        options={{
                          fieldKey: 'field',
                          nameKey: 'field_name',
                          labelKey: 'field_label',
                          fieldColSize: 4,
                          operatorColSize: 3,
                          valueColSize: 5,
                          useSearchableDropdown: true,
                          valueFields: filterValueFields || availableFields,
                          allowFieldValue: true,
                          layoutObjectName: objectName,
                          onPatch: onPatchFilter
                            ? (idx, patch) => {
                                const row = externalFilters[idx];
                                if (row) onPatchFilter(row.id, patch);
                              }
                            : null,
                        }}
                        index={index}
                        readOnly={resourcesLoading}
                        picklistPrefetchByFieldId={picklistOptionsByFieldId}
                      />
                      {showDateHint && (
                        <DateLiteralsHintLink onOpen={() => setShowDateHelpModal(true)} />
                      )}
                    </Col>
                  </Row>
                </div>
              );
            })}
          </div>
        )}

        <div className="filter-actions" style={{
          marginTop: 'auto',
          paddingTop: '16px',
          borderTop: '1px solid #e5e5e5',
          display: 'flex',
          gap: '8px'
        }}>
          <Button
            variant="outline-secondary"
            onClick={onClearFilters}
            style={{ flex: 1 }}
            disabled={resourcesLoading}
          >
            Clear All
          </Button>
          <Button
            variant="primary"
            onClick={onApplyFilters}
            style={{ flex: 1 }}
            disabled={resourcesLoading}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ListViewFilterPanel;
